import React from "react";
import { useFormState } from "react-final-form";

import {
  FontIcon,
  Heading,
  mergeStyles,
  Stack,
  useTheme
} from "@bps/fluent-ui";
import { Frequency } from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { ExpanderButton } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/ExpanderButton.tsx";
import { AppointmentFormValues } from "@shared-types/booking/appointment-form-values.types.ts";
import { SecondColumnContent } from "@shared-types/booking/second-column-content.enum.ts";
import { useStores } from "@stores/hooks/useStores.ts";

import { RecurringAppointmentConflicts } from "./RecurringAppointmentConflicts.tsx";
import { getEndSchedduleTypeText, getRecurrenceNaturalText } from "./utils.ts";

export const RepeatDetailsButton: React.FunctionComponent = () => {
  const { booking } = useStores();

  const theme = useTheme();
  const {
    values: {
      repeat,
      interval,
      frequency,
      dayRecur,
      monthYearRecurrence,
      endScheduleType,
      until,
      count,
      recurrenceOccurred,
      startTime,
      startDate,
      providerId
    }
  } = useFormState<AppointmentFormValues>({ subscription: { values: true } });

  const onClick = () => {
    booking.ui.setSecondColumnContent(
      booking.ui.currentAppointment?.secondColumnContent !==
        SecondColumnContent.repeat
        ? SecondColumnContent.repeat
        : undefined
    );
  };

  const showHideRecurringConflicts =
    startDate &&
    startTime &&
    providerId &&
    interval &&
    frequency &&
    ((dayRecur && dayRecur.length > 0) || monthYearRecurrence);

  const getButtonText = () => {
    if (!startDate || !startTime || !providerId) {
      return "Select provider, date, and time to set up repeat";
    } else if (
      interval &&
      frequency &&
      ((dayRecur && dayRecur.length > 0) || monthYearRecurrence)
    ) {
      const data = {
        startDate,
        frequency,
        interval,
        dayRecur,
        endScheduleType,
        until,
        count,
        recurrenceOccurred,
        monthYearRecurrence
      };

      return `Repeats ${getRecurrenceNaturalText(
        data
      )} ${getEndSchedduleTypeText(data)}`;
    } else {
      return "Set up repeat appointment";
    }
  };

  const showAsterix = () => {
    if (frequency === Frequency.Week && !(dayRecur && dayRecur.length > 0))
      return true;
    else if (frequency === Frequency.Month && !monthYearRecurrence) return true;
    else return false;
  };

  const disabledOrNot = !(startDate && startTime && providerId && repeat);

  const showHideDisabledColorText =
    !startDate || !startTime || !providerId || disabledOrNot
      ? theme.semanticColors.disabledText
      : theme.palette.themePrimary;

  const isRepeatSecondColumn =
    booking.ui.currentAppointment?.secondColumnContent ===
    SecondColumnContent.repeat;

  return (
    <ExpanderButton<AppointmentFormValues>
      fields={[
        "frequency",
        "dayRecur",
        "monthYearRecurrence",
        "count",
        "until"
      ]}
      errorMessage="Required fields remain in this section"
      id="appt-form-repeat-button"
      onClick={onClick}
      isOpened={isRepeatSecondColumn}
      disabled={disabledOrNot}
      styles={{
        root: {
          minHeight: 0,
          maxWidth: "100%",
          margin: 0
        }
      }}
    >
      <Stack
        verticalFill
        grow
        horizontalAlign="baseline"
        verticalAlign="baseline"
      >
        <Stack
          horizontal
          verticalAlign="center"
          styles={{ root: { width: "100%" } }}
          tokens={{ childrenGap: 8 }}
        >
          <Stack.Item shrink>
            <FontIcon
              className={mergeStyles({
                color: showHideDisabledColorText,
                fontSize: "14px"
              })}
              iconName="History"
            />
          </Stack.Item>
          <Stack.Item grow styles={{ root: { textAlign: "start" } }}>
            <Heading
              hasAsterisk={showAsterix()}
              disabled={
                !startDate || !startTime || !providerId || disabledOrNot
              }
            >
              {getButtonText()}
            </Heading>
          </Stack.Item>
        </Stack>

        {showHideRecurringConflicts && (
          <Stack.Item
            grow
            styles={{ root: { width: "100%", textAlign: "start" } }}
          >
            <RecurringAppointmentConflicts
              hideInitialStateRrule={true}
              isDetailedConflictVisible={false}
            />
          </Stack.Item>
        )}
      </Stack>
    </ExpanderButton>
  );
};
